import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { AuthShell, Field, inputCls } from "@/components/nadia/AuthShell";
import { useAuth } from "@/lib/auth";
import { authRegister, getBillingConfig, formatApiError } from "@/lib/api";

const PLAN_LABEL = { start: "Start · €19,90/mese + IVA · 100 gettoni", pro: "Pro · €39,90/mese + IVA · 250 gettoni" };

export default function Registrati() {
  const [params] = useSearchParams();
  const plan = ["start", "pro"].includes(params.get("plan")) ? params.get("plan") : "";
  const pack = params.get("pack") || "";
  const demoUrl = sessionStorage.getItem("nadia_demo_url") || "";
  const [f, setF] = useState({ name: "", email: "", password: "", password_confirm: "", accept_terms: false, accept_privacy: false });
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const { login, user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { getBillingConfig().then(setCfg).catch(() => {}); }, []);
  useEffect(() => { if (user) nav(user.email_verified ? "/app" : "/verifica-email", { replace: true }); }, [user, nav]);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.password_confirm) return toast.error("Le password non coincidono.");
    if (!f.accept_terms || !f.accept_privacy) return toast.error("Accetta Termini e Privacy per continuare.");
    setBusy(true);
    try {
      const r = await authRegister({ ...f, demo_url: demoUrl || null, pending_plan: plan || null, pending_pack: pack || null });
      if (typeof window.gtag === "function") {
        window.gtag("event", "sign_up", {
          method: "email"
        });
      }
      if (plan) sessionStorage.setItem("nadia_pending", JSON.stringify({ plan, pack: pack || null }));
      login(r.token, r.account);
      toast.success(r.message, { duration: 5000 });
      if (r.email_sent) toast("Ultimo passo: conferma la tua email per attivare l'account" + (plan ? " e completare il pagamento" : ""), { duration: 6000 });
      else toast.error(`Invio email di verifica non riuscito: ${r.email_error || "errore sconosciuto"}. Puoi richiedere un nuovo invio.`, { duration: 8000 });
      nav("/verifica-email", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const packInfo = pack && cfg ? cfg.packs.find((p) => p.id === pack) : null;

  return (
    <AuthShell
      eyebrow="Registrazione"
      title="Crea il tuo account Nadia Cloud"
      subtitle="Bastano nome, email e password per creare il tuo account."
      footer={<>Hai già un account? <Link to="/accedi" className="text-cyan-300 hover:text-cyan-200" data-testid="register-go-login">Accedi</Link></>}
    >
      {(plan || demoUrl) && (
        <div className="mb-5 space-y-2">
          {plan && (
            <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/[0.05] px-3 py-2 text-xs text-slate-200 flex items-center gap-2" data-testid="register-plan-badge">
              <Sparkles size={13} className="text-cyan-300" /> Hai scelto: <span className="font-semibold">{PLAN_LABEL[plan] || plan}</span>{packInfo && <> · {packInfo.credits} crediti €{packInfo.price.toFixed(2).replace(".", ",")}</>}
            </div>
          )}
          {demoUrl && (
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] px-3 py-2 text-xs text-slate-200 flex items-center gap-2" data-testid="register-demo-badge">
              <Check size={13} className="text-emerald-300 shrink-0" /> <span>Hai indicato <span className="font-semibold break-all">{demoUrl}</span>: il link sarà associato al tuo account.</span>
            </div>
          )}
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Nome"><Input value={f.name} onChange={set("name")} required className={inputCls} placeholder="Il tuo nome" data-testid="register-name" /></Field>
        <Field label="Email"><Input type="email" value={f.email} onChange={set("email")} required className={inputCls} placeholder="nome@azienda.it" data-testid="register-email" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Password"><Input type="password" value={f.password} onChange={set("password")} required minLength={8} className={inputCls} placeholder="Almeno 8 caratteri" data-testid="register-password" /></Field>
          <Field label="Conferma password"><Input type="password" value={f.password_confirm} onChange={set("password_confirm")} required className={inputCls} placeholder="Ripeti la password" data-testid="register-password-confirm" /></Field>
        </div>
        <label className="flex items-start gap-2.5 text-sm text-slate-300 cursor-pointer">
          <Checkbox checked={f.accept_terms} onCheckedChange={set("accept_terms")} className="mt-0.5 border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400" data-testid="register-terms" />
          <span>Accetto <Link to="/termini" className="text-cyan-300 underline" target="_blank" rel="noopener noreferrer">Termini e condizioni</Link></span>
        </label>
        <label className="flex items-start gap-2.5 text-sm text-slate-300 cursor-pointer">
          <Checkbox checked={f.accept_privacy} onCheckedChange={set("accept_privacy")} className="mt-0.5 border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400" data-testid="register-privacy" />
          <span>Ho letto l’<Link to="/privacy" className="text-cyan-300 underline" target="_blank" rel="noopener noreferrer">Informativa sulla privacy</Link></span>
        </label>
        <p className="text-xs text-amber-300" data-testid="register-legal-unavailable">Documenti consultabili in anteprima come bozze in revisione; pubblicazione definitiva ancora da completare.</p>
        <Button type="submit" disabled={busy} className="w-full h-12 bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-300 hover:to-violet-400 text-slate-950 font-semibold rounded-xl" data-testid="register-submit">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
          {plan === "start" || plan === "pro" ? "Crea account e attiva il piano" : plan === "flex" ? "Crea account e attiva Flex" : plan === "flex" && pack ? "Crea account e acquista crediti" : "Crea il mio account"}
        </Button>
        <div className="text-[11px] text-slate-500 text-center">Dopo la registrazione ti chiedo di confermare l'email. Il pagamento{plan ? " del piano scelto" : ""} avviene solo dopo la verifica.</div>
      </form>
    </AuthShell>
  );
}
